import React, { useEffect, useState } from 'react';
import { PlusIcon, PuzzleIcon, UploadCloudIcon, SparklesIcon, LinkIcon, Loader2Icon, Trash2Icon, XIcon, SearchIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useSkillStore } from '../stores/skillStore';
import { useModelStore } from '../stores/modelStore';
import { useUiStore } from '../stores/uiStore';
import { Skill } from '../types';
import { PageContainer, PageHeader, EmptyState, SkillStatusBadge, SkillSourceBadge } from '../components/common/page';
import { Card, Button, Badge, Input, Select, Label, Textarea } from '../components/common/ui';
import { Modal } from '../components/common/Modal';
import { Markdown } from '../components/common/Markdown';
import { formatRelative } from '../utils/format';
type AddMode = 'upload' | 'ai' | 'url' | null;
export function SkillCenterPage() {
  useScreenInit();
  const skills = useSkillStore(s => s.skills);
  const {
    toggleSkill,
    deleteSkill
  } = useSkillStore();
  const pushToast = useUiStore(s => s.pushToast);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [detail, setDetail] = useState<Skill | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const filtered = skills.filter(s => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (query && !`${s.name}${s.description}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const handleDelete = (s: Skill) => {
    if (!deleteSkill(s.skillId)) {
      pushToast('error', `「${s.name}」被 ${s.usedByAgentCount} 个 Agent 引用，请先解除引用`);
    } else {
      pushToast('success', 'Skill 已删除');
    }
  };
  return <PageContainer data-id="element-844">
      <PageHeader title="Skills 中心" description="上传、AI 创建或从第三方链接安装 Skill，统一管理与复用" action={<>
            <Button variant="outline" size="sm" onClick={() => setAddMode('upload')} data-id="element-846">
              <UploadCloudIcon className="h-4 w-4" data-id="element-847" /> 上传
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAddMode('url')} data-id="element-848">
              <LinkIcon className="h-4 w-4" data-id="element-849" /> 链接安装
            </Button>
            <Button size="sm" onClick={() => setAddMode('ai')} data-id="element-850">
              <SparklesIcon className="h-4 w-4" data-id="element-851" /> AI 创建
            </Button>
          </>} data-id="element-845" />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row" data-id="element-852">
        <div className="relative flex-1" data-id="element-853">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-854" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="搜索 Skill 名称或简介" className="pl-9" data-id="element-855" />
        </div>
        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="sm:w-40" data-id="element-856">
          <option value="all" data-id="element-857">全部状态</option>
          <option value="enabled" data-id="element-858">已启用</option>
          <option value="disabled" data-id="element-859">已禁用</option>
          <option value="installing" data-id="element-860">安装中</option>
          <option value="failed" data-id="element-861">失败</option>
        </Select>
      </div>

      {filtered.length === 0 ? <EmptyState icon={PuzzleIcon} title="暂无 Skill" description="通过上传、AI 创建或第三方链接安装第一个 Skill" data-id="element-862" /> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-863">
          {filtered.map(s => <Card key={s.skillId} className="flex flex-col p-4" data-id="element-864">
              <div className="flex items-start justify-between gap-2" data-id="element-865">
                <div className="flex items-center gap-2" data-id="element-866">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800" data-id="element-867">
                    {s.status === 'installing' ? <Loader2Icon className="h-4 w-4 animate-spin text-amber-400" data-id="element-868" /> : <PuzzleIcon className="h-4 w-4 text-purple-400" data-id="element-869" />}
                  </div>
                  <button onClick={() => setDetail(s)} className="text-left" data-id="element-870">
                    <div className="font-medium text-slate-100 hover:text-indigo-300" data-id="element-871">
                      {s.name}
                    </div>
                    <div className="font-mono text-xs text-slate-500" data-id="element-872">
                      v{s.version}
                    </div>
                  </button>
                </div>
                <SkillStatusBadge status={s.status} data-id="element-873" />
              </div>
              <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-400" data-id="element-874">
                {s.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-875">
                <SkillSourceBadge source={s.source} data-id="element-876" />
                {s.tags.slice(0, 2).map(t => <Badge key={t} tone="slate" data-id="element-877">
                    {t}
                  </Badge>)}
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-slate-800 pt-3 text-xs text-slate-500" data-id="element-878">
                <span data-id="element-879">
                  被 {s.usedByAgentCount} 个 Agent 引用 ·{' '}
                  {formatRelative(s.updatedAt)}
                </span>
              </div>
              {s.status !== 'installing' && <div className="mt-3 flex gap-2" data-id="element-880">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => toggleSkill(s.skillId)} data-id="element-881">
                    {s.status === 'enabled' ? '禁用' : '启用'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(s)} className="text-red-400 hover:text-red-300" aria-label="删除" data-id="element-882">
                    <Trash2Icon className="h-3.5 w-3.5" data-id="element-883" />
                  </Button>
                </div>}
            </Card>)}
        </div>}

      <UploadModal open={addMode === 'upload'} onClose={() => setAddMode(null)} data-id="element-884" />
      <AiModal open={addMode === 'ai'} onClose={() => setAddMode(null)} data-id="element-885" />
      <UrlModal open={addMode === 'url'} onClose={() => setAddMode(null)} data-id="element-886" />
      <SkillDetailDrawer skill={detail} onClose={() => setDetail(null)} data-id="element-887" />
    </PageContainer>;
}
function UploadModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const installFromUpload = useSkillStore(s => s.installFromUpload);
  const pushToast = useUiStore(s => s.pushToast);
  const [fileName, setFileName] = useState('');
  const [enableAfter, setEnableAfter] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  useEffect(() => {
    if (open) setFileName('');
  }, [open]);
  const submit = () => {
    if (!fileName) return;
    installFromUpload(fileName, enableAfter);
    pushToast('info', 'Skill 上传成功，正在安装');
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="上传 Skill" description="支持 zip / tar.gz，最大 50MB" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-889">
            取消
          </Button>
          <Button onClick={submit} disabled={!fileName} data-id="element-890">
            开始安装
          </Button>
        </>} data-id="element-888">
      <div onDragOver={e => {
      e.preventDefault();
      setDragOver(true);
    }} onDragLeave={() => setDragOver(false)} onDrop={e => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) setFileName(f.name);
    }} className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${dragOver ? 'border-indigo-500 bg-indigo-500/5' : 'border-slate-700'}`} data-id="element-891">
        <UploadCloudIcon className="h-8 w-8 text-slate-500" data-id="element-892" />
        <p className="mt-2 text-sm text-slate-300" data-id="element-893">拖拽压缩包到此处，或</p>
        <label className="mt-2 cursor-pointer text-sm font-medium text-indigo-400 hover:text-indigo-300" data-id="element-894">
          选择文件
          <input type="file" className="hidden" accept=".zip,.tar.gz,.tgz" onChange={e => setFileName(e.target.files?.[0]?.name || '')} data-id="element-895" />
        </label>
        {fileName && <p className="mt-3 rounded-md bg-slate-800 px-3 py-1 font-mono text-xs text-slate-300" data-id="element-896">
            {fileName}
          </p>}
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-slate-300" data-id="element-897">
        <input type="checkbox" checked={enableAfter} onChange={e => setEnableAfter(e.target.checked)} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-898" />
        安装后立即启用
      </label>
    </Modal>;
}
function AiModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const generateWithAi = useSkillStore(s => s.generateWithAi);
  const models = useModelStore(s => s.models.filter(m => m.enabled));
  const pushToast = useUiStore(s => s.pushToast);
  const [prompt, setPrompt] = useState('');
  const [nameHint, setNameHint] = useState('');
  const [modelId, setModelId] = useState(models[0]?.modelId || '');
  const [enableAfter, setEnableAfter] = useState(true);
  useEffect(() => {
    if (open) {
      setPrompt('');
      setNameHint('');
      setModelId(models[0]?.modelId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = () => {
    if (!prompt.trim()) return;
    generateWithAi({
      prompt: prompt.trim(),
      nameHint: nameHint.trim(),
      modelId,
      enableAfterInstall: enableAfter
    });
    pushToast('info', 'Skill Builder Agent 已开始创建 Skill');
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="AI 创建 Skill" description="描述需求，由 Skill Builder Agent 自动生成 Skill" size="lg" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-900">
            取消
          </Button>
          <Button onClick={submit} disabled={!prompt.trim()} data-id="element-901">
            <SparklesIcon className="h-4 w-4" data-id="element-902" /> 开始生成
          </Button>
        </>} data-id="element-899">
      <div className="space-y-4" data-id="element-903">
        <div data-id="element-904">
          <Label required data-id="element-905">需求提示词</Label>
          <Textarea rows={4} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="创建一个用于分析 Nginx 日志并生成异常访问报告的 Skill" data-id="element-906" />
        </div>
        <div data-id="element-907">
          <Label data-id="element-908">名称提示</Label>
          <Input value={nameHint} onChange={e => setNameHint(e.target.value)} placeholder="Nginx 日志分析 Skill" data-id="element-909" />
        </div>
        <div data-id="element-910">
          <Label data-id="element-911">参考模型</Label>
          <Select value={modelId} onChange={e => setModelId(e.target.value)} data-id="element-912">
            {models.map(m => <option key={m.modelId} value={m.modelId} data-id="element-913">
                {m.name}
              </option>)}
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300" data-id="element-914">
          <input type="checkbox" checked={enableAfter} onChange={e => setEnableAfter(e.target.checked)} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-915" />
          生成后立即启用
        </label>
      </div>
    </Modal>;
}
function UrlModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const installFromUrl = useSkillStore(s => s.installFromUrl);
  const pushToast = useUiStore(s => s.pushToast);
  const [url, setUrl] = useState('');
  const [sha, setSha] = useState('');
  const [enableAfter, setEnableAfter] = useState(true);
  useEffect(() => {
    if (open) {
      setUrl('');
      setSha('');
    }
  }, [open]);
  const submit = () => {
    if (!url.trim()) return;
    installFromUrl({
      url: url.trim(),
      expectedSha256: sha.trim() || undefined,
      enableAfterInstall: enableAfter
    });
    pushToast('info', '已开始下载并安装第三方 Skill');
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="第三方链接安装" description="输入下载链接，系统将下载、安全校验并安装" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-917">
            取消
          </Button>
          <Button onClick={submit} disabled={!url.trim()} data-id="element-918">
            开始安装
          </Button>
        </>} data-id="element-916">
      <div className="space-y-4" data-id="element-919">
        <div data-id="element-920">
          <Label required data-id="element-921">下载链接</Label>
          <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/skills/nginx-log-skill.zip" className="font-mono" data-id="element-922" />
        </div>
        <div data-id="element-923">
          <Label data-id="element-924">期望 SHA256 (可选)</Label>
          <Input value={sha} onChange={e => setSha(e.target.value)} placeholder="用于校验文件完整性" className="font-mono" data-id="element-925" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300" data-id="element-926">
          <input type="checkbox" checked={enableAfter} onChange={e => setEnableAfter(e.target.checked)} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-927" />
          安装后立即启用
        </label>
      </div>
    </Modal>;
}
function SkillDetailDrawer({
  skill,
  onClose
}: {
  skill: Skill | null;
  onClose: () => void;
}) {
  if (!skill) return null;
  return <Modal open={!!skill} onClose={onClose} title={skill.name} size="lg" data-id="element-928">
      <div className="space-y-4" data-id="element-929">
        <div className="flex flex-wrap items-center gap-2" data-id="element-930">
          <Badge tone="slate" data-id="element-931">v{skill.version}</Badge>
          <SkillSourceBadge source={skill.source} data-id="element-932" />
          <SkillStatusBadge status={skill.status} data-id="element-933" />
          {skill.author && <span className="text-xs text-slate-500" data-id="element-934">作者：{skill.author}</span>}
        </div>
        <p className="text-sm text-slate-300" data-id="element-935">{skill.description}</p>
        {skill.capabilities.length > 0 && <div data-id="element-936">
            <Label data-id="element-937">能力</Label>
            <div className="flex flex-wrap gap-1.5" data-id="element-938">
              {skill.capabilities.map(c => <Badge key={c} tone="purple" data-id="element-939">
                  {c}
                </Badge>)}
            </div>
          </div>}
        {skill.dependencies && skill.dependencies.length > 0 && <div data-id="element-940">
            <Label data-id="element-941">依赖</Label>
            <div className="flex flex-wrap gap-1.5" data-id="element-942">
              {skill.dependencies.map(d => <Badge key={d} tone="slate" data-id="element-943">
                  <span className="font-mono" data-id="element-944">{d}</span>
                </Badge>)}
            </div>
          </div>}
        {skill.downloadUrl && <div data-id="element-945">
            <Label data-id="element-946">安装来源</Label>
            <p className="break-all font-mono text-xs text-slate-400" data-id="element-947">
              {skill.downloadUrl}
            </p>
          </div>}
        {skill.readme && <div data-id="element-948">
            <Label data-id="element-949">README</Label>
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-id="element-950">
              <Markdown content={skill.readme} data-id="element-951" />
            </div>
          </div>}
      </div>
    </Modal>;
}